// Диалог создания валюты (extract из CurrenciesTab, SLED-EXAM-FE-V17-W5).

import { useEffect } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Zod-схема создания валюты: ISO-3 + символ ≤8. */
const currencyCreateSchema = z.object({
  code: z.string().trim().regex(/^[A-Za-z]{3}$/, 'Код — 3 латинские буквы'),
  symbol: z.string().trim().min(1, 'Символ обязателен').max(8, 'Слишком длинный символ'),
});

/** Значения формы создания валюты. */
type CurrencyCreateValues = z.infer<typeof currencyCreateSchema>;

/** Пустые значения формы создания (сброс при открытии). */
const EMPTY_CREATE: CurrencyCreateValues = { code: '', symbol: '' };

/** Пропсы диалога создания валюты. */
export interface CurrencyCreateDialogProps {
  /** Открыт ли диалог. */
  open: boolean;
  /** Закрыть без сохранения. */
  onClose: () => void;
  /** Валидный submit: code уже в верхнем регистре. */
  onSubmit: (values: { code: string; symbol: string }) => void;
  /** Блокировка кнопки «Создать» на время мутации. */
  isPending?: boolean;
  /** Ошибка POST; показывается Alert в диалоге. */
  error?: unknown;
}

/**
 * Диалог создания валюты.
 * Контракт: ISO-3 код + символ; submit отдаёт `code` в UPPERCASE; ru zod-литералы без i18n.
 */
export function CurrencyCreateDialog({
  open,
  onClose,
  onSubmit,
  isPending = false,
  error,
}: CurrencyCreateDialogProps): JSX.Element {
  const form = useForm<CurrencyCreateValues>({
    resolver: zodResolver(currencyCreateSchema),
    defaultValues: EMPTY_CREATE,
  });
  const { reset, control, handleSubmit } = form;
  const hasError = Boolean(error);

  useEffect(() => {
    if (open) {
      reset(EMPTY_CREATE);
    }
  }, [open, reset]);

  const submitCreate: SubmitHandler<CurrencyCreateValues> = (values) => {
    onSubmit({ code: values.code.toUpperCase(), symbol: values.symbol });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('dict.currencies.createTitle')}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(submitCreate)} noValidate>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {hasError ? (
              <Alert severity="error">
                {extractApiErrorMessage(error, t('dict.currencies.saveError'))}
              </Alert>
            ) : null}
            <Controller
              name="code"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('dict.currencies.code')}
                  required
                  fullWidth
                  inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="symbol"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('dict.currencies.symbol')}
                  required
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Stack>
          <DialogActions>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={isPending}>
              {t('common.create')}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
